import { useState, useCallback, useEffect } from 'react';
import { FiUser } from 'react-icons/fi';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export type AvatarSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl';

interface AvatarProps {
  /** Image URL — if empty/falsy, fallback is shown immediately */
  src?: string | null;
  /** Alt text for accessibility */
  alt?: string;
  /** Display name used for initials fallback */
  name?: string;
  /** Size preset */
  size?: AvatarSize;
  /** Additional className for the outer container */
  className?: string;
  /** Additional className for the image (when rendered) */
  imgClassName?: string;
  /** Additional className for the fallback container */
  fallbackClassName?: string;
  /** Optional ring/border styling */
  ring?: boolean;
  /** Optional border color class */
  borderColor?: string;
  /** Optional background color for fallback */
  fallbackBg?: string;
  /** Optional icon size override (defaults based on size) */
  iconSize?: number;
}

const sizeMap: Record<AvatarSize, { container: string; icon: number }> = {
  xs: { container: 'w-7 h-7', icon: 14 },
  sm: { container: 'w-9 h-9', icon: 16 },
  md: { container: 'w-10 h-10', icon: 18 },
  lg: { container: 'w-12 h-12', icon: 20 },
  xl: { container: 'w-14 h-14', icon: 24 },
  '2xl': { container: 'w-[72px] h-[72px]', icon: 32 },
  '3xl': { container: 'w-[96px] h-[96px]', icon: 40 },
  '4xl': { container: 'w-[128px] h-[128px]', icon: 52 },
  '5xl': { container: 'w-[160px] h-[160px]', icon: 64 },
};

function getInitials(name?: string): string {
  if (!name?.trim()) return '';
  return name
    .trim()
    .split(/\s+/)
    .map((w) => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

/**
 * Reusable Avatar component with automatic fallback on image error.
 * Shows a user icon or initials when no image is available or the image fails to load.
 */
export function Avatar({
  src,
  alt = '',
  name,
  size = 'md',
  className,
  imgClassName,
  fallbackClassName,
  ring = false,
  borderColor = 'border-zinc-700',
  fallbackBg = 'bg-zinc-800',
  iconSize,
}: AvatarProps) {
  const [hasError, setHasError] = useState(false);
  const { container, icon } = sizeMap[size];
  const showImage = src && !hasError;
  const initials = getInitials(name);

  // Reset error state when src changes (e.g. user selects a new image preview)
  useEffect(() => {
    setHasError(false);
  }, [src]);

  const handleError = useCallback(() => {
    setHasError(true);
  }, []);

  return (
    <div
      className={cn(
        container,
        'rounded-full overflow-hidden shrink-0 flex items-center justify-center',
        showImage ? '' : fallbackBg,
        borderColor,
        ring && 'ring-2 ring-white/10',
        className,
      )}
    >
      {showImage ? (
        <img
          src={src}
          alt={alt}
          className={cn('w-full h-full object-cover', imgClassName)}
          onError={handleError}
          loading="lazy"
        />
      ) : initials ? (
        <span
          className={cn(
            'text-zinc-400 font-bold select-none',
            size === 'xs' && 'text-xs',
            size === 'sm' && 'text-sm',
            size === 'md' && 'text-base',
            size === 'lg' && 'text-lg',
            size === 'xl' && 'text-xl',
            size === '2xl' && 'text-2xl',
            size === '3xl' && 'text-3xl',
            size === '4xl' && 'text-4xl',
            size === '5xl' && 'text-5xl',
            fallbackClassName,
          )}
        >
          {initials}
        </span>
      ) : (
        <FiUser size={iconSize ?? icon} className="text-zinc-500" />
      )}
    </div>
  );
}

/**
 * Cover image component with automatic fallback to a gradient on error.
 */
interface CoverImageProps {
  src?: string | null;
  alt?: string;
  className?: string;
  imgClassName?: string;
  fallbackClassName?: string;
  children?: React.ReactNode;
}

export function CoverImage({
  src,
  alt = '',
  className,
  imgClassName,
  fallbackClassName,
  children,
}: CoverImageProps) {
  const [hasError, setHasError] = useState(false);
  const showImage = src && !hasError;

  // Reset error state when src changes
  useEffect(() => {
    setHasError(false);
  }, [src]);

  const handleError = useCallback(() => {
    setHasError(true);
  }, []);

  return (
    <div className={cn('w-full h-full overflow-hidden relative', className)}>
      {showImage ? (
        <img
          src={src}
          alt={alt}
          className={cn('w-full h-full object-cover', imgClassName)}
          onError={handleError}
        />
      ) : (
        <div
          className={cn('w-full h-full', fallbackClassName)}
          style={{
            background: fallbackClassName
              ? undefined
              : 'linear-gradient(135deg, #1a0533 0%, #0f0f1a 40%, #1a1040 100%)',
          }}
        />
      )}
      {children}
    </div>
  );
}
